import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

function Culture() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const images = [
    { src: "img1.png", alt: "Happy member", size: "small" },
    { src: "img2.png", alt: "Yoga practice", size: "medium" },
    { src: "img3.png", alt: "Wellness journey", size: "medium" },
    { src: "img4.png", alt: "Meditation session", size: "large" },
    { src: "img5.png", alt: "Group class", size: "small" },
    { src: "img6.png", alt: "Healthy lifestyle", size: "large" },
    { src: "img7.png", alt: "Mindful practice", size: "medium" },
    { src: "img8.png", alt: "Community", size: "small" },
  ];

  return (
    <section className="py-20 lg:py-32 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-10 right-10 w-64 h-64 bg-sage-100 rounded-full blur-3xl opacity-50"></div>
      <div className="absolute bottom-10 left-10 w-64 h-64 bg-earth-100 rounded-full blur-3xl opacity-50"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-sage-100 text-sage-700 rounded-full text-sm font-semibold mb-4">
            Our Community
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Transforming{" "}
            <span className="gradient-text">Hundreds of Lives</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Join our vibrant community of wellness seekers and experience the
            transformation
          </p>
        </motion.div>

        {/* Image Gallery Grid */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6"
        >
          {images.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
              }}
              className={`
                ${image.size === "large" ? "col-span-2 row-span-2" : ""}
                ${image.size === "medium" ? "col-span-1 row-span-1" : ""}
                ${image.size === "small" ? "col-span-1 row-span-1" : ""}
                group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300
              `}
            >
              <div className="relative h-full">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                {/* Overlay on Hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-sage-900/80 via-sage-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-semibold text-lg">
                      {image.alt}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {[
            { number: "100+", label: "Happy Members", icon: "😊" },
            { number: "500+", label: "Classes Held", icon: "🧘‍♀️" },
            { number: "8+", label: "Services", icon: "⭐" },
            { number: "5.0", label: "Average Rating", icon: "🏆" },
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.9 + index * 0.1 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-sage-400 to-teal-400 mb-4">
                <span className="text-3xl">{stat.icon}</span>
              </div>
              <div className="text-3xl lg:text-4xl font-bold gradient-text mb-2">
                {stat.number}
              </div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default Culture;
